import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { supabase } from '../../api/supabase';
import { useNavigation } from '@react-navigation/native';

export const BackupExportScreen: React.FC = () => {
  const navigation = useNavigation();
  const [exporting, setExporting] = useState<string | null>(null);

  const exportData = async (tableName: string, fileName: string) => {
    if (Platform.OS !== 'web') {
      Alert.alert('Error', 'Export functionality is only available on web platform');
      return;
    }

    setExporting(tableName);
    try {
      console.log(`📦 Exporting ${tableName}...`);
      
      // Fetch data from Supabase
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        window.alert(`No Data\n\nNo records found in ${tableName}`);
        setExporting(null);
        return;
      }

      // Convert to CSV
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            const value = row[header];
            // Handle null, quotes, and commas
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.alert(
        `تم التصدير\nExport Successful\n\n` +
        `تم تصدير ${data.length} سجل\n${data.length} records exported`
      );
      
      console.log(`✅ Exported ${data.length} records from ${tableName}`);
    } catch (error: any) {
      console.error(`❌ Error exporting ${tableName}:`, error);
      window.alert(`خطأ\nError\n\nفشل تصدير البيانات\n${error.message}`);
    } finally {
      setExporting(null);
    }
  };

  const exportActivityLog = () => exportData('activity_log', `activity_log_${new Date().toISOString().split('T')[0]}.csv`);
  const exportAssignments = () => exportData('assignment_history', `assignments_${new Date().toISOString().split('T')[0]}.csv`);
  const exportVehicles = () => exportData('vehicles', `vehicles_${new Date().toISOString().split('T')[0]}.csv`);
  const exportDrivers = () => exportData('drivers', `drivers_${new Date().toISOString().split('T')[0]}.csv`);
  const exportAccounting = () => exportData('accounting', `accounting_${new Date().toISOString().split('T')[0]}.csv`);
  const exportDeliveryForms = () => exportData('vehicle_delivery_forms', `delivery_forms_${new Date().toISOString().split('T')[0]}.csv`);
  const exportReceivingForms = () => exportData('vehicle_receiving_forms', `receiving_forms_${new Date().toISOString().split('T')[0]}.csv`);

  const exportOptions = [
    {
      title: 'Activity Log',
      description: 'Export all system activity logs',
      icon: 'time-outline',
      onPress: exportActivityLog,
      key: 'activity_log',
    },
    {
      title: 'Assignments',
      description: 'Export assignment history',
      icon: 'calendar-outline',
      onPress: exportAssignments,
      key: 'assignment_history',
    },
    {
      title: 'Vehicles',
      description: 'Export vehicle records',
      icon: 'car-outline',
      onPress: exportVehicles,
      key: 'vehicles',
    },
    {
      title: 'Drivers',
      description: 'Export driver records',
      icon: 'person-outline',
      onPress: exportDrivers,
      key: 'drivers',
    },
    {
      title: 'Accounting',
      description: 'Export accounting transactions',
      icon: 'cash-outline',
      onPress: exportAccounting,
      key: 'accounting',
    },
    {
      title: 'Delivery Forms',
      description: 'Export vehicle delivery forms',
      icon: 'document-text-outline',
      onPress: exportDeliveryForms,
      key: 'vehicle_delivery_forms',
    },
    {
      title: 'Receiving Forms',
      description: 'Export vehicle receiving forms',
      icon: 'documents-outline',
      onPress: exportReceivingForms,
      key: 'vehicle_receiving_forms',
    },
  ];

  const handleFullBackup = async () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Error', 'Backup functionality is only available on web platform');
      return;
    }

    // Use window.confirm on web instead of Alert.alert
    const confirmed = window.confirm(
      'Full System Backup\n\nThis will export all data. This may take a few minutes.\n\nContinue?'
    );
    
    if (!confirmed) return;

    setExporting('full_backup');
    try {
      // Export all tables sequentially
      for (const option of exportOptions) {
        await exportData(option.key, `backup_${option.key}_${new Date().toISOString().split('T')[0]}.csv`);
        // Small delay between exports
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      window.alert('Full backup completed successfully!\n\nAll data has been exported as CSV files.');
    } catch (error: any) {
      console.error('❌ Full backup error:', error);
      window.alert(`Backup failed: ${error.message}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>النسخ الاحتياطي والتصدير / Backup & Export</Text>
      </View>

      <ScrollView style={styles.content}>
        {/* Full Backup Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Full System Backup</Text>
          <TouchableOpacity
            style={[styles.fullBackupCard, exporting === 'full_backup' && styles.disabledCard]}
            onPress={handleFullBackup}
            disabled={exporting !== null}
          >
            <View style={styles.fullBackupIcon}>
              <Ionicons name="cloud-download-outline" size={32} color={theme.colors.primary} />
            </View>
            <View style={styles.fullBackupContent}>
              <Text style={styles.fullBackupTitle}>Export All Data</Text>
              <Text style={styles.fullBackupDescription}>
                Create a complete backup of all system data
              </Text>
            </View>
            {exporting === 'full_backup' ? (
              <View style={styles.loadingIndicator}>
                <Text style={styles.loadingText}>Exporting...</Text>
              </View>
            ) : (
              <Ionicons name="chevron-forward" size={24} color={theme.colors.gray400} />
            )}
          </TouchableOpacity>
        </View>

        {/* Individual Exports Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Individual Data Exports</Text>
          <Text style={styles.sectionSubtitle}>Export specific data tables as CSV files</Text>
          
          {exportOptions.map((option, index) => (
            <TouchableOpacity
              key={index}
              style={[styles.exportCard, exporting === option.key && styles.disabledCard]}
              onPress={option.onPress}
              disabled={exporting !== null}
            >
              <View style={styles.exportIcon}>
                <Ionicons name={option.icon as any} size={24} color={theme.colors.primary} />
              </View>
              <View style={styles.exportContent}>
                <Text style={styles.exportTitle}>{option.title}</Text>
                <Text style={styles.exportDescription}>{option.description}</Text>
              </View>
              {exporting === option.key ? (
                <View style={styles.loadingIndicator}>
                  <Text style={styles.loadingText}>...</Text>
                </View>
              ) : (
                <Ionicons name="download-outline" size={20} color={theme.colors.gray400} />
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Info Section */}
        <View style={styles.infoSection}>
          <Ionicons name="information-circle-outline" size={20} color={theme.colors.info} />
          <Text style={styles.infoText}>
            Exported files are in CSV format and can be opened in Excel or any spreadsheet application.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  content: {
    flex: 1,
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 12,
  },
  fullBackupCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  fullBackupIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: theme.colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  fullBackupContent: {
    flex: 1,
  },
  fullBackupTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: 4,
  },
  fullBackupDescription: {
    fontSize: 14,
    color: theme.colors.gray700,
  },
  exportCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  exportIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  exportContent: {
    flex: 1,
  },
  exportTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  exportDescription: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  disabledCard: {
    opacity: 0.5,
  },
  loadingIndicator: {
    paddingHorizontal: 12,
  },
  loadingText: {
    fontSize: 14,
    color: theme.colors.gray600,
    fontStyle: 'italic',
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: theme.colors.infoLight,
    padding: 16,
    margin: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.info,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.gray700,
    marginLeft: 12,
    lineHeight: 20,
  },
});
